/* eslint-disable @typescript-eslint/naming-convention */
import { format } from 'date-fns';
import React, { useContext } from 'react';

import type { MatchInfo, Reference } from '@swf/cms';
import { CMSContext, CMSHTML } from '@swf/cms';
import Translation from '@swf/components/Translation/Translation';
import type { TranslationTag } from '@swf/core/types/i18n';

import styles from './MatchDetails.scss';

interface MatchDetailsProps {
  dataType: Nullable<'pregame' | 'postgame'>;
  dataRef: Reference[];
  previewTranslation: TranslationTag;
  reportTranslation: TranslationTag;
}

const MatchDetails: React.FC<MatchDetailsProps> = ({
  dataType,
  dataRef,
  previewTranslation,
  reportTranslation
}) => {
  const cmsContext = useContext(CMSContext);

  const matchInfo = dataRef.filter(({ uid }) => {
    const matchInfo = cmsContext[uid] as MatchInfo;

    return matchInfo?.info_type === dataType;
  });

  if (!matchInfo) {
    return null;
  }

  const postgame = dataType === 'postgame';
  const type = postgame ? (
    <Translation {...reportTranslation} />
  ) : (
    <Translation {...previewTranslation} />
  );

  return (
    <>
      {matchInfo.map(({ uid }, index) => {
        const { game_date, content } = cmsContext[uid] as MatchInfo;

        if (!content) {
          return null;
        }

        const gameDate = format(new Date(game_date), 'LLLL d');
        const hashGameDate = format(new Date(game_date), 'yyyy-MM-dd');
        return (
          <div key={index} id={`${dataType}-${hashGameDate}`}>
            <p className={styles.title}>
              {type} | {gameDate}
            </p>
            {dataType === 'pregame' && (
              <>
                <CMSHTML className={styles.title} content={content.main_title as string} />
                <CMSHTML className={styles.title} content={content.main_headline as string} />
                <CMSHTML content={content.intro_segment as string} />
                <CMSHTML content={content.home_team as string} />
                <CMSHTML content={content.away_team as string} />
                <CMSHTML content={content.conclusion_segment as string} />
                <CMSHTML content={content.generated_prediction as string} />
                <CMSHTML content={content.did_you_know as string} />
                <CMSHTML content={content.outro_segment as string} />
              </>
            )}
            {dataType === 'postgame' && (
              <>
                <CMSHTML
                  className={styles.reportHeader}
                  content={content.scorers_segment as string}
                />
                <CMSHTML className={styles.title} content={content.headline as string} />
                <CMSHTML content={content.game_summary as string} />
                <CMSHTML content={content.common_stats as string} />
                <CMSHTML content={content.notable_stats as string} />
                <CMSHTML content={content.game_outcome_consequence as string} />
                <CMSHTML content={content.top_performer as string} />
                <CMSHTML content={content.next_up as string} />
              </>
            )}
          </div>
        );
      })}
    </>
  );
};

export default MatchDetails;
